// frontend/src/pages/NetworkPage.tsx
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Badge, Button, Card, Col, Row, Tabs, Tag, Typography, Table, Switch, Select,
  Segmented, Spin, Alert, Space, Tooltip,
} from 'antd';
import { SaveOutlined, WifiOutlined, PhoneOutlined } from '@ant-design/icons';
import {
  Area, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip,
  Legend, ResponsiveContainer, Line, ComposedChart,
} from 'recharts';
import {
  networkApi, NetworkDevice, NetworkSummary, RoutingRule, TrafficPoint, WanPoint,
  WanProvider, DhcpLease,
} from '../api/network';

const { Title, Text } = Typography;

// ── helpers ───────────────────────────────────────────────────────────────────

function bytesToMbps(bytes: number): number {
  return parseFloat(((bytes * 8) / 1_000_000).toFixed(3));
}

// Адаптивный формат скорости: Кб/с для малых значений, Мб/с для больших
function fmtSpeed(mbps: number): string {
  if (mbps < 0.1) return `${(mbps * 1000).toFixed(1)} Кб/с`;
  return `${mbps.toFixed(2)} Мб/с`;
}

function formatTs(ts: string): string {
  const d = new Date(ts);
  return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
}

// Группа по sort_order: 1-10 = Адм/офис, 20-29 = Сервис/магазины, 40-49 = Охрана, 99+ = Инфраструктура
function deviceGroup(sortOrder: number): string {
  if (sortOrder <= 10) return 'Администрация и офис';
  if (sortOrder <= 29) return 'Сервис и магазины';
  if (sortOrder <= 49) return 'Охрана';
  return 'Инфраструктура';
}

// Дополнительная подсказка под названием устройства.
// Показываем только: телефоны (добавочный номер), Мастер, МикроТик, Видеонаблюдение.
function getDeviceHint(dev: NetworkDevice): string | null {
  const desc = dev.description;
  if (!desc) return null;
  const dobMatch = desc.match(/доб\.?\s*(\d+)/);
  if (dobMatch) return `доб. ${dobMatch[1]}`;
  const keepNames = ['Мастер', 'MikroTik hEX', 'Видеонаблюдение'];
  if (keepNames.includes(dev.name)) return desc;
  return null;
}

// ── WAN provider card ─────────────────────────────────────────────────────────

const WanCard: React.FC<{
  provider: WanProvider | null;
  up: boolean | null;
  latency: number | null;
  speed?: { in_mbps: number; out_mbps: number } | null;
  onClick?: () => void;
}> = ({ provider, up, latency, speed, onClick }) => (
  <Card
    size="small"
    style={{ minWidth: 220, cursor: onClick ? 'pointer' : undefined }}
    hoverable={!!onClick}
    onClick={onClick}
  >
    <Space direction="vertical" size={2} style={{ width: '100%' }}>
      <Space>
        <Tag color={provider?.wan_key === 'WAN2' ? 'blue' : 'green'} style={{ margin: 0 }}>
          {provider?.wan_key ?? '—'}
        </Tag>
        <Text strong>{provider?.name ?? '—'}</Text>
        <Badge
          status={up === null ? 'default' : up ? 'success' : 'error'}
          text={up === null ? '—' : up ? 'UP' : 'DOWN'}
        />
      </Space>
      {speed != null && (
        <Space size={12}>
          <Text style={{ fontSize: 12 }}>↓ {fmtSpeed(speed.in_mbps)}</Text>
          <Text style={{ fontSize: 12 }}>↑ {fmtSpeed(speed.out_mbps)}</Text>
        </Space>
      )}
      {latency !== null && latency !== undefined && (
        <Text type="secondary" style={{ fontSize: 12 }}>{latency.toFixed(1)} мс</Text>
      )}
      {provider?.phone && (
        <Text style={{ fontSize: 12 }}><PhoneOutlined style={{ marginRight: 4 }} />{provider.phone}</Text>
      )}
      {provider?.account_number && (
        <Text type="secondary" style={{ fontSize: 12 }}>л/с {provider.account_number}</Text>
      )}
      {provider?.device_model && (
        <Text type="secondary" style={{ fontSize: 12 }}>{provider.device_model} · {provider.organization}</Text>
      )}
    </Space>
  </Card>
);

// ── Overview tab ──────────────────────────────────────────────────────────────

interface OverviewTabProps {
  devices: NetworkDevice[];
  summary: NetworkSummary | null;
  providers: WanProvider[];
  dhcp: DhcpLease[];
  onDeviceClick: (viewKey: string) => void;
}

// Строка таблицы — либо реальное устройство, либо заголовок группы
type DeviceRow = NetworkDevice & { _isGroupHeader?: false };
type GroupHeaderRow = { _isGroupHeader: true; _groupName: string; id: string; sort_order: number };
type TableRow = DeviceRow | GroupHeaderRow;

function buildDeviceRows(devices: NetworkDevice[]): TableRow[] {
  const rows: TableRow[] = [];
  let currentGroup = '';
  for (const dev of devices) {
    const group = deviceGroup(dev.sort_order);
    if (group !== currentGroup) {
      currentGroup = group;
      rows.push({ _isGroupHeader: true, _groupName: group, id: `group_${group}`, sort_order: dev.sort_order });
    }
    rows.push({ ...dev, _isGroupHeader: false });
  }
  return rows;
}

const OverviewTab: React.FC<OverviewTabProps> = ({ devices, summary, providers, dhcp, onDeviceClick }) => {
  const wan1Provider = providers.find(p => p.wan_key === 'WAN1') ?? null;
  const wan2Provider = providers.find(p => p.wan_key === 'WAN2') ?? null;
  const rows = buildDeviceRows(devices);

  const [rules, setRules] = useState<RoutingRule[]>([]);
  const [toggling, setToggling] = useState<string | null>(null);
  // pending: rule.id → желаемый isWan2 (true = WAN2, false = WAN1)
  const [pendingChanges, setPendingChanges] = useState<Map<string, boolean>>(new Map());
  // pending creates: IP-адреса устройств без правила, которые хотим перевести на WAN2
  const [pendingCreates, setPendingCreates] = useState<Set<string>>(new Set());
  const [wanSpeeds, setWanSpeeds] = useState<{
    wan1: { in_mbps: number; out_mbps: number } | null;
    wan2: { in_mbps: number; out_mbps: number } | null;
  }>({ wan1: null, wan2: null });

  useEffect(() => {
    networkApi.getRouting().then(setRules).catch(() => {});
    networkApi.getWan('24h').then(rows => {
      // После перезапуска backend первый poll = 0 (нет предыдущего счётчика).
      // Берём среднее последних 5 ненулевых точек для стабильного значения.
      const nonZero = rows.slice(-20).filter(r => r.wan1_in + r.wan1_out + r.wan2_in + r.wan2_out > 0);
      if (nonZero.length > 0) {
        const pts = nonZero.slice(-5);
        const avg = (vals: number[]) => vals.reduce((a, b) => a + b, 0) / vals.length;
        setWanSpeeds({
          wan1: { in_mbps: avg(pts.map(r => r.wan1_in)) * 8 / 60 / 1_000_000, out_mbps: avg(pts.map(r => r.wan1_out)) * 8 / 60 / 1_000_000 },
          wan2: { in_mbps: avg(pts.map(r => r.wan2_in)) * 8 / 60 / 1_000_000, out_mbps: avg(pts.map(r => r.wan2_out)) * 8 / 60 / 1_000_000 },
        });
      }
    }).catch(() => {});
  }, []);

  const ruleByIp = useMemo(() => {
    const m = new Map<string, RoutingRule>();
    for (const r of rules) m.set(r.src_address, r);
    return m;
  }, [rules]);

  // Возвращает эффективный isWan2 с учётом несохранённых изменений
  const getEffectiveIsWan2 = useCallback((rule: RoutingRule | undefined): boolean => {
    if (!rule?.id) return false;
    if (pendingChanges.has(rule.id)) return pendingChanges.get(rule.id)!;
    return !rule.disabled;
  }, [pendingChanges]);

  // Переключение — только в локальном состоянии, без API
  const handleToggle = (rule: RoutingRule, checked: boolean) => {
    if (!rule.id) return;
    const originalIsWan2 = !rule.disabled;
    setPendingChanges(prev => {
      const next = new Map(prev);
      if (checked === originalIsWan2) {
        next.delete(rule.id!); // вернули в исходное — убираем из pending
      } else {
        next.set(rule.id!, checked);
      }
      return next;
    });
  };

  // Сохранение — создаём новые правила + применяем pending изменения
  const handleSave = async () => {
    setToggling('saving');
    try {
      // Создаём правила для устройств без существующих правил
      if (pendingCreates.size > 0) {
        await Promise.all(
          Array.from(pendingCreates).map(ip => networkApi.createRouting(ip)),
        );
      }
      // Переключаем существующие правила
      if (pendingChanges.size > 0) {
        await Promise.all(
          Array.from(pendingChanges.entries()).map(([ruleId, isWan2]) =>
            networkApi.patchRouting(ruleId, !isWan2),
          ),
        );
      }
      // Обновляем список правил с MikroTik
      const newRules = await networkApi.getRouting();
      setRules(newRules);
      setPendingChanges(new Map());
      setPendingCreates(new Set());
    } finally {
      setToggling(null);
    }
  };

  const deviceColumns = [
    {
      title: 'Организация',
      key: 'org',
      width: 130,
      render: (_: unknown, row: TableRow) => {
        if ((row as GroupHeaderRow)._isGroupHeader) {
          return (
            <Text strong style={{ color: '#888', fontSize: 12, textTransform: 'uppercase', letterSpacing: 1 }}>
              {(row as GroupHeaderRow)._groupName}
            </Text>
          );
        }
        return <Text type="secondary" style={{ fontSize: 12 }}>{(row as DeviceRow).organization ?? '—'}</Text>;
      },
    },
    {
      title: 'Название',
      key: 'name',
      width: 190,
      render: (_: unknown, row: TableRow) => {
        if ((row as GroupHeaderRow)._isGroupHeader) return null;
        const dev = row as DeviceRow;
        const hint = getDeviceHint(dev);
        return (
          <Space direction="vertical" size={0}>
            <Text>{dev.name}</Text>
            {hint && <Text type="secondary" style={{ fontSize: 11 }}>{hint}</Text>}
          </Space>
        );
      },
    },
    {
      title: 'Статус',
      key: 'status',
      width: 160,
      render: (_: unknown, row: TableRow) => {
        if ((row as GroupHeaderRow)._isGroupHeader) return null;
        const dev = row as DeviceRow;
        if (dev.is_online === null) return <Badge status="default" text="—" />;
        if (!dev.is_online) return <Badge status="error" text="offline" />;
        const bIn = dev.bytes_in ?? 0;
        const bOut = dev.bytes_out ?? 0;
        const hasTraffic = bIn > 0 || bOut > 0;
        return (
          <Space direction="vertical" size={0}>
            <Badge status="success" text="online" />
            {hasTraffic && (
              <Text type="secondary" style={{ fontSize: 11 }}>
                ↓ {fmtSpeed(bIn * 8 / 60 / 1_000_000)} · ↑ {fmtSpeed(bOut * 8 / 60 / 1_000_000)}
              </Text>
            )}
          </Space>
        );
      },
    },
    {
      title: 'IP',
      key: 'ip',
      width: 120,
      render: (_: unknown, row: TableRow) => {
        if ((row as GroupHeaderRow)._isGroupHeader) return null;
        return <code style={{ fontSize: 12 }}>{(row as DeviceRow).ip}</code>;
      },
    },
    {
      title: 'MAC',
      key: 'mac',
      width: 145,
      render: (_: unknown, row: TableRow) => {
        if ((row as GroupHeaderRow)._isGroupHeader) return null;
        const mac = (row as DeviceRow).mac_address;
        return mac
          ? <code style={{ fontSize: 12 }}>{mac}</code>
          : <Text type="secondary">—</Text>;
      },
    },
    {
      title: 'По умолч.',
      key: 'default_wan',
      width: 105,
      render: (_: unknown, row: TableRow) => {
        if ((row as GroupHeaderRow)._isGroupHeader) return null;
        const wan = (row as DeviceRow).default_wan;
        return (
          <Tag color={wan === 'WAN2' ? 'blue' : 'green'} style={{ fontSize: 11 }}>
            {wan === 'WAN2' ? 'WAN2' : 'WAN1'}
          </Tag>
        );
      },
    },
    {
      title: 'Текущий канал',
      key: 'current_wan',
      width: 140,
      render: (_: unknown, row: TableRow) => {
        if ((row as GroupHeaderRow)._isGroupHeader) return null;
        const dev = row as DeviceRow;
        const rule = ruleByIp.get(dev.ip);
        // Показываем реальное состояние (до сохранения не меняется)
        const isWan2 = !!(rule?.id && !rule.disabled);
        return (
          <Tag color={isWan2 ? 'blue' : 'green'} style={{ fontSize: 11 }}>
            {isWan2 ? 'WAN2 Билайн' : 'WAN1 Wifire'}
          </Tag>
        );
      },
    },
    {
      title: 'WAN',
      key: 'wan_switch',
      width: 115,
      render: (_: unknown, row: TableRow) => {
        if ((row as GroupHeaderRow)._isGroupHeader) return null;
        const dev = row as DeviceRow;
        if (dev.ip === '192.168.0.1') {
          return <Tooltip title="Шлюз — не управляется"><Text type="secondary">—</Text></Tooltip>;
        }
        const rule = ruleByIp.get(dev.ip);
        if (!rule?.id) {
          // Нет правила на MikroTik — можно создать через UI
          const isPendingCreate = pendingCreates.has(dev.ip);
          return (
            <div onClick={e => e.stopPropagation()}>
              <Switch
                size="small"
                checkedChildren="WAN2"
                unCheckedChildren="WAN1"
                checked={isPendingCreate}
                loading={toggling === 'saving'}
                onChange={checked => {
                  setPendingCreates(prev => {
                    const next = new Set(prev);
                    if (checked) next.add(dev.ip);
                    else next.delete(dev.ip);
                    return next;
                  });
                }}
              />
            </div>
          );
        }
        const isWan2 = getEffectiveIsWan2(rule);
        return (
          <div onClick={e => e.stopPropagation()}>
            <Switch
              size="small"
              checkedChildren="WAN2"
              unCheckedChildren="WAN1"
              checked={isWan2}
              loading={toggling === 'saving'}
              onChange={checked => handleToggle(rule, checked)}
            />
          </div>
        );
      },
    },
    {
      title: 'Пинг',
      key: 'latency',
      width: 80,
      render: (_: unknown, row: TableRow) => {
        if ((row as GroupHeaderRow)._isGroupHeader) return null;
        const dev = row as DeviceRow;
        if (!dev.is_online || dev.latency_ms === null) return null;
        return <Text type="secondary" style={{ fontSize: 12 }}>{dev.latency_ms.toFixed(1)} мс</Text>;
      },
    },
  ];

  const dhcpColumns = [
    { title: 'Организация', key: 'org', width: 130, render: () => <Text type="secondary" style={{ fontSize: 12 }}>—</Text> },
    {
      title: 'Имя хоста',
      key: 'name',
      width: 190,
      render: (_: unknown, l: DhcpLease) => (
        <Space direction="vertical" size={0}>
          <Text>{l.hostname ?? '—'}</Text>
          {l.mac && <Text type="secondary" style={{ fontSize: 11 }}>{l.mac}</Text>}
        </Space>
      ),
    },
    {
      title: 'Статус',
      key: 'status',
      width: 160,
      render: (_: unknown, l: DhcpLease) => (
        <Badge status={l.status === 'bound' ? 'success' : 'default'} text={l.status === 'bound' ? 'online' : l.status} />
      ),
    },
    { title: 'IP', dataIndex: 'ip', key: 'ip', width: 120, render: (ip: string) => <code style={{ fontSize: 12 }}>{ip}</code> },
    { title: 'MAC', dataIndex: 'mac', key: 'mac', width: 145, render: (mac: string | null) => mac ? <code style={{ fontSize: 12 }}>{mac}</code> : <Text type="secondary">—</Text> },
    { title: 'По умолч.', key: 'default_wan', width: 105, render: () => <Tag color="green" style={{ fontSize: 11 }}>WAN1</Tag> },
    { title: 'Текущий канал', key: 'current_wan', width: 140, render: () => <Tag color="green" style={{ fontSize: 11 }}>WAN1 Wifire</Tag> },
    {
      title: 'WAN',
      key: 'wan_switch',
      width: 115,
      render: () => (
        <Tooltip title="DHCP клиент — управление недоступно">
          <Switch size="small" checkedChildren="WAN2" unCheckedChildren="WAN1" checked={false} disabled />
        </Tooltip>
      ),
    },
    { title: 'Пинг', key: 'latency', width: 80, render: () => null },
  ];

  return (
    <div>
      {/* WAN статус */}
      <Row gutter={12} style={{ marginBottom: 20 }}>
        <Col>
          <WanCard
            provider={wan1Provider}
            up={summary?.wan1_up ?? null}
            latency={summary?.wan1_latency_ms ?? null}
            speed={wanSpeeds.wan1}
            onClick={() => onDeviceClick('wan1')}
          />
        </Col>
        <Col>
          <WanCard
            provider={wan2Provider}
            up={summary?.wan2_up ?? null}
            latency={summary?.wan2_latency_ms ?? null}
            speed={wanSpeeds.wan2}
            onClick={() => onDeviceClick('wan2')}
          />
        </Col>
        {summary?.last_poll && (
          <Col style={{ display: 'flex', alignItems: 'center' }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Обновлено: {new Date(summary.last_poll).toLocaleTimeString('ru-RU')}
            </Text>
          </Col>
        )}
      </Row>

      {/* Уведомление о несохранённых изменениях WAN */}
      {(pendingChanges.size > 0 || pendingCreates.size > 0) && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={
            <Space>
              <span>Есть несохранённые изменения маршрутизации ({pendingChanges.size + pendingCreates.size} шт.) — нажмите «Сохранить», чтобы применить</span>
              <Button
                type="primary"
                size="small"
                icon={<SaveOutlined />}
                loading={toggling === 'saving'}
                onClick={handleSave}
              >
                Сохранить
              </Button>
            </Space>
          }
        />
      )}

      {/* Статичные устройства */}
      <Table
        dataSource={rows}
        columns={deviceColumns}
        rowKey="id"
        pagination={false}
        size="small"
        scroll={{ x: 1280 }}
        style={{ marginBottom: dhcp.length > 0 ? 0 : 24 }}
        onRow={(row) => {
          if ((row as GroupHeaderRow)._isGroupHeader) {
            return { style: { background: '#f5f5f5' } };
          }
          const dev = row as DeviceRow;
          const rule = ruleByIp.get(dev.ip);
          const hasPending = !!(rule?.id && pendingChanges.has(rule.id)) || pendingCreates.has(dev.ip);
          return {
            style: hasPending
              ? { background: '#fff3e0' }
              : undefined,
          };
        }}
      />

      {/* DHCP клиенты */}
      {dhcp.length > 0 && (
        <Table
          dataSource={dhcp}
          columns={dhcpColumns}
          rowKey="id"
          pagination={false}
          size="small"
          showHeader={false}
          scroll={{ x: 1280 }}
          style={{ marginBottom: 24, borderTop: '1px solid #f0f0f0' }}
          onRow={(l: DhcpLease) => ({
            style: { cursor: 'pointer' },
            onClick: () => onDeviceClick(`dhcp_${l.ip}`),
          })}
          title={() => (
            <Text strong style={{ color: '#888', fontSize: 12, textTransform: 'uppercase', letterSpacing: 1 }}>
              DHCP клиенты ({dhcp.length})
            </Text>
          )}
        />
      )}

      {/* События */}
      {summary && summary.events.length > 0 && (
        <div>
          <Title level={5}>Последние события</Title>
          {summary.events.map(ev => (
            <Alert
              key={ev.id}
              type={ev.type.endsWith('DOWN') ? 'error' : 'success'}
              message={`${ev.type} — ${new Date(ev.occurred_at).toLocaleString('ru-RU')}`}
              style={{ marginBottom: 8 }}
              showIcon
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ── Traffic tab ───────────────────────────────────────────────────────────────

interface TrafficTabProps {
  devices: NetworkDevice[];
  dhcp: DhcpLease[];
  initView?: string;
}

const TrafficTab: React.FC<TrafficTabProps> = ({ devices, dhcp, initView }) => {
  const [period, setPeriod] = useState<'24h' | '30d'>('24h');
  const [view, setView] = useState<string>(initView || 'wan1');
  const [data, setData] = useState<(TrafficPoint | WanPoint)[]>([]);
  const [loading, setLoading] = useState(false);

  const staticOptions = devices.map(d => ({ value: `dev_${d.id}`, label: `${d.name} (${d.ip})` }));
  const dhcpOptions = dhcp.map(l => ({
    value: `dhcp_${l.ip}`,
    label: `${l.hostname ?? l.mac ?? l.ip} — DHCP (${l.ip})`,
    disabled: true,
  }));

  const viewOptions = [
    { value: 'wan1', label: 'WAN1 (Wifire)' },
    { value: 'wan2', label: 'WAN2 (Билайн)' },
    ...staticOptions,
    ...(dhcpOptions.length > 0 ? [{ value: '__sep__', label: '── DHCP клиенты (нет данных) ──', disabled: true }, ...dhcpOptions] : []),
  ];

  const load = useCallback(async () => {
    if (view === '__sep__') return;
    setLoading(true);
    try {
      if (view === 'wan1' || view === 'wan2') {
        setData(await networkApi.getWan(period));
      } else if (view.startsWith('dev_')) {
        const devId = parseInt(view.replace('dev_', ''));
        setData(await networkApi.getDeviceHistory(devId, period));
      } else {
        setData([]);
      }
    } finally {
      setLoading(false);
    }
  }, [view, period]);

  useEffect(() => { load(); }, [load]);

  const isIndividualWan = view === 'wan1' || view === 'wan2';

  // Данные для индивидуального или обычного графика
  const chartData = data.map(r => {
    if (isIndividualWan) {
      const wp = r as WanPoint;
      return {
        ts: formatTs(r.ts),
        '↓': bytesToMbps(view === 'wan1' ? wp.wan1_in : wp.wan2_in),
        '↑': bytesToMbps(view === 'wan1' ? wp.wan1_out : wp.wan2_out),
        latency: view === 'wan1' ? wp.wan1_latency_ms : wp.wan2_latency_ms,
      };
    }
    return {
      ts: formatTs(r.ts),
      'Загрузка': bytesToMbps((r as TrafficPoint).bytes_in),
      'Отдача': bytesToMbps((r as TrafficPoint).bytes_out),
      latency: (r as TrafficPoint).latency_ms,
    };
  });

  // Совместный график (оба канала), показывается ниже индивидуального
  const combinedChartData = isIndividualWan ? data.map(r => {
    const wp = r as WanPoint;
    return {
      ts: formatTs(r.ts),
      'WAN1 ↓': bytesToMbps(wp.wan1_in),
      'WAN1 ↑': bytesToMbps(wp.wan1_out),
      'WAN2 ↓': bytesToMbps(wp.wan2_in),
      'WAN2 ↑': bytesToMbps(wp.wan2_out),
    };
  }) : [];

  return (
    <div>
      <Space style={{ marginBottom: 16 }} wrap>
        <Segmented
          options={[{ label: '24 часа', value: '24h' }, { label: '30 дней', value: '30d' }]}
          value={period}
          onChange={v => setPeriod(v as '24h' | '30d')}
        />
        <Select style={{ width: 300 }} options={viewOptions} value={view} onChange={setView} />
      </Space>
      {loading ? (
        <Spin />
      ) : isIndividualWan ? (
        <>
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={chartData} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="ts" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
              <YAxis yAxisId="left" tickFormatter={v => `${v} Мб/с`} tick={{ fontSize: 11 }} />
              <YAxis yAxisId="right" orientation="right" tickFormatter={v => `${v} мс`} tick={{ fontSize: 11 }} />
              <ReTooltip formatter={(value: number, name: string) =>
                name === 'latency' ? [`${value} мс`, 'Latency'] : [`${value} Мб/с`, name]
              } />
              <Legend />
              <Area yAxisId="left" type="monotone" dataKey="↓" fill="#1890ff33" stroke="#1890ff" />
              <Area yAxisId="left" type="monotone" dataKey="↑" fill="#52c41a33" stroke="#52c41a" />
              <Line yAxisId="right" type="monotone" dataKey="latency" stroke="#fa8c16" dot={false} strokeDasharray="4 2" name="Latency" />
            </ComposedChart>
          </ResponsiveContainer>
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 20, marginBottom: 8 }}>
            Оба канала
          </Text>
          <ResponsiveContainer width="100%" height={200}>
            <ComposedChart data={combinedChartData} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="ts" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
              <YAxis tickFormatter={v => `${v} Мб/с`} tick={{ fontSize: 11 }} />
              <ReTooltip formatter={(value: number, name: string) => [`${value} Мб/с`, name]} />
              <Legend />
              <Area type="monotone" dataKey="WAN1 ↓" fill="#1890ff33" stroke="#1890ff" />
              <Area type="monotone" dataKey="WAN1 ↑" fill="#52c41a33" stroke="#52c41a" />
              <Area type="monotone" dataKey="WAN2 ↓" fill="#722ed133" stroke="#722ed1" />
              <Area type="monotone" dataKey="WAN2 ↑" fill="#fa8c1633" stroke="#fa8c16" />
            </ComposedChart>
          </ResponsiveContainer>
        </>
      ) : (
        <ResponsiveContainer width="100%" height={350}>
          <ComposedChart data={chartData} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="ts" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
            <YAxis yAxisId="left" tickFormatter={v => `${v} Мб/с`} tick={{ fontSize: 11 }} />
            <YAxis yAxisId="right" orientation="right" tickFormatter={v => `${v} мс`} tick={{ fontSize: 11 }} />
            <ReTooltip formatter={(value: number, name: string) =>
              name.startsWith('latency') ? [`${value} мс`, name] : [`${value} Мб/с`, name]
            } />
            <Legend />
            <Area yAxisId="left" type="monotone" dataKey="Загрузка" fill="#1890ff33" stroke="#1890ff" />
            <Area yAxisId="left" type="monotone" dataKey="Отдача" fill="#52c41a33" stroke="#52c41a" />
            <Line yAxisId="right" type="monotone" dataKey="latency" stroke="#fa8c16" dot={false} strokeDasharray="4 2" name="Latency" />
          </ComposedChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────

const NetworkPage: React.FC = () => {
  const [devices, setDevices] = useState<NetworkDevice[]>([]);
  const [summary, setSummary] = useState<NetworkSummary | null>(null);
  const [providers, setProviders] = useState<WanProvider[]>([]);
  const [dhcp, setDhcp] = useState<DhcpLease[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<string>('overview');
  const [trafficInitView, setTrafficInitView] = useState<string>('wan');
  const [trafficKey, setTrafficKey] = useState<number>(0);

  const refresh = useCallback(async () => {
    const [devs, sum, provs, leases] = await Promise.all([
      networkApi.getDevices(),
      networkApi.getSummary(),
      networkApi.getWanProviders().catch(() => [] as WanProvider[]),
      networkApi.getDhcpLeases().catch(() => [] as DhcpLease[]),
    ]);
    setDevices(devs);
    setSummary(sum);
    setProviders(provs);
    setDhcp(leases);
    setLoading(false);
  }, []);

  useEffect(() => {
    refresh();
    const timer = setInterval(refresh, 60_000);
    return () => clearInterval(timer);
  }, [refresh]);

  const handleDeviceClick = useCallback((viewKey: string) => {
    setTrafficInitView(viewKey);
    setTrafficKey(k => k + 1);
    setActiveTab('traffic');
  }, []);

  const items = [
    {
      key: 'overview',
      label: 'Обзор',
      children: loading
        ? <Spin />
        : <OverviewTab
            devices={devices}
            summary={summary}
            providers={providers}
            dhcp={dhcp}
            onDeviceClick={handleDeviceClick}
          />,
    },
    {
      key: 'traffic',
      label: 'Трафик',
      children: <TrafficTab key={trafficKey} initView={trafficInitView} devices={devices} dhcp={dhcp} />,
    },
  ];

  return (
    <div>
      <Title level={3} style={{ marginBottom: 16 }}>
        <WifiOutlined style={{ marginRight: 8 }} />
        Мониторинг сети
      </Title>
      <Tabs activeKey={activeTab} onChange={setActiveTab} items={items} />
    </div>
  );
};

export default NetworkPage;
